import { PCard } from './people';
import { Avatar, Badge, Mono } from './primitives';
import { fmtDuration } from '../lib/duration';
import { useOnTheClock } from '../lib/usePresence';

// Who is working right now. Deliberately NOT an attention count
// (docs/UI-CONVENTIONS.md): nobody needs to act on this, so it carries no
// warning tone and no badge on the sidebar — it is a status board, not a queue.
//
// The Phase 3 critique of this card (it was never audited) found, and this
// fixes: a header that counted people on a break as "in"; a bare "25m" beside
// each name with nothing saying of what; rows that were divs in a fixed
// four-track grid, where a long name pushed the badge off the card; and a
// private duration format. Not fixed here, because the reader is outside this
// file: listOnTheClock() answers a failed read with [], so a failure still
// renders as "Nobody is on the clock".

export function OnTheClockCard() {
  // The poll lives in lib/usePresence, shared with the People roster pip.
  const { rows, now } = useOnTheClock();
  const working = rows?.filter((r) => r.state === 'in').length ?? 0;
  const paused = (rows?.length ?? 0) - working;

  return (
    <PCard
      label="On the clock"
      right={
        rows?.length ? (
          // Two facets of the list below, so "in" means working, not "clocked in
          // but away".
          <Mono dim>
            {working} working{paused > 0 && ` · ${paused} on a break`}
          </Mono>
        ) : null
      }
    >
      {rows === null ? (
        <div style={{ fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink-3)' }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink-3)' }}>
          Nobody is on the clock.
        </div>
      ) : (
        <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
          {rows.map((r) => {
            const span = fmtDuration((now - new Date(r.since).getTime()) / 1000, 'hm');
            return (
              <li
                key={r.profile_id}
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 'var(--hf-space-2) var(--hf-space-3)',
                  padding: 'var(--hf-space-2) 0',
                  borderBottom: '1px solid var(--hf-rule)',
                }}
              >
                <Avatar name={r.full_name ?? '—'} size={24} />
                <span
                  style={{
                    flex: '1 1 8rem',
                    minWidth: 0,
                    fontSize: 'var(--hf-text-md)',
                    color: 'var(--hf-ink)',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {r.full_name ?? 'Someone'}
                </span>
                {/* How long, of what: "working 2h 14m" or "on a break 12m". A
                    break is neutral — being on one is nothing to act on, and
                    amber is for what waits on someone. */}
                <Badge tone={r.state === 'in' ? 'success' : 'neutral'}>
                  {r.state === 'in' ? 'working' : 'on a break'}
                </Badge>
                <Mono dim style={{ minWidth: '4rem', textAlign: 'right' }}>
                  {span}
                </Mono>
              </li>
            );
          })}
        </ul>
      )}
    </PCard>
  );
}
